/**
 * xyz DEX WS sync — real-time HIP-3 open orders.
 *
 * xyz **account state** needs no subscription here: `useHyperMainSync`'s
 * `allDexsClearinghouseState` delivers every dex's clearinghouseState
 * (including xyz) in one push and writes the `accountXyz` cache + merged
 * `account` cache. Open orders, however, are still a per-dex topic, so this
 * hook owns the xyz portion:
 *
 *   - `openOrders({ user, dex: "xyz" })` → the `__dex: "xyz"` slice of the
 *     `openOrders` cache
 *
 * REST queries in `useHyperAccount` / `useHyperOpenOrders` remain as a
 * 10-minute backstop. Market meta (`metaAndAssetCtxs`) stays REST-only for
 * the universe (szDecimals, maxLeverage, etc.); contexts go real-time via
 * `useTradeMetaSync`.
 *
 * Mounted by `components/hyperliquid/hyper-user-events-sync.tsx` (same as
 * `useHyperMainSync`).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useHyperliquidSubscription } from "#/features/trade/hooks/use-hyperliquid-subscription";
import { stripDexPrefix, XYZ_DEX_NAME } from "#/features/trade/lib/hyperliquid";
import {
	isRateLimitError,
	isWebSocketClosedError,
} from "#/features/trade/lib/hyperliquid-error";
import { queryKeys } from "./query-keys";
import {
	captureXyzDiagnostic,
	type OpenOrder,
} from "./use-hyperliquid-account";
import { useWallet } from "./use-wallet";

export function useHyperXyzSync() {
	const { address } = useWallet();
	const queryClient = useQueryClient();

	// Transient WS drop rejects the in-flight subscribe; auto-resubscribe
	// recovers it. Not the HIP-3 balance loss this diagnostic watches for.
	// Also passed as SubscriptionOptions.onError (SDK 0.33) so a confirmed
	// subscription dying post-reconnect is surfaced too.
	const onError = (error: unknown) => {
		if (isWebSocketClosedError(error)) return;
		captureXyzDiagnostic("ws-openOrders-failed", "error", {
			address,
			error: error instanceof Error ? error.message : String(error),
			rateLimited: isRateLimitError(error),
		});
	};

	useHyperliquidSubscription({
		enabled: address !== null,
		deps: [address],
		onError,
		subscribe: (client) =>
			client.openOrders(
				{ user: address as `0x${string}`, dex: XYZ_DEX_NAME },
				(event) => {
					if (!address) return;
					const xyzOrders: OpenOrder[] = (event.orders as OpenOrder[]).map(
						(o) => ({
							...o,
							__dex: "xyz" as const,
							coin: stripDexPrefix(o.coin),
						}),
					);

					queryClient.setQueryData<OpenOrder[]>(
						queryKeys.hyper.openOrders(address),
						(old) => {
							// Optimistic placeholders (oid < 0) live in their own bucket;
							// excluding them from nonXyz prevents duplicate rows.
							const nonXyz = (old ?? []).filter(
								(o) => o.__dex !== "xyz" && o.oid >= 0,
							);
							const optimistic = (old ?? []).filter((o) => o.oid < 0);
							return [...optimistic, ...nonXyz, ...xyzOrders];
						},
					);
				},
				{ onError },
			),
	});
}
